/**
 * Similar Jobs Panel Component - Unified Structure
 * 
 * Displays jobs with similar analysis results using the same Jobs/Evidence tab structure
 * as the casefile spots tab, but scoped to a specific job's similar results.
 */

import React, { useState, useMemo } from 'react';
import { FaSync } from 'react-icons/fa';
import useSimilarJobs from '../../../../hooks/useSimilarJobs';
import UnifiedSimilarResults from '../UnifiedSimilarResults';
import UniversalEvidenceWindow from '../../../evidence/UniversalEvidenceWindow';
import { getEvidenceActions } from '../../../evidence/evidenceActionConfigs';
import '../../styles/results-ui.css';

const SimilarJobsPanel = ({ 
  currentJobId, 
  currentResults, 
  onJobSelect,
  className = '',
  showStatistics = true,
  defaultExpanded = false,
  searchOptions: searchOptionsOverride = null,
  onRefreshResultsToEvidence = null, // NEW: Callback when Results button clicked from evidence modal
  currentUser = null, // Pass current user for privacy checks
  onCloseParentModal = null, // Callback to close the parent Results modal
  onOpenEvidence = null // Callback to open evidence (handled by parent)
}) => {
  const [activeTab, setActiveTab] = useState('jobs'); // 'jobs' or 'evidence'

  const {
    similarJobs,
    loading,
    error,
    refresh,
    hasResults,
    isEmpty
  } = useSimilarJobs({
    currentJobId,
    currentResults,
    autoLoad: true, // Always load for similar jobs
    searchOptions: {
      maxResults: 15,
      minSimilarityScore: 0.25,
      searchRadius: 'user',
      ...(searchOptionsOverride || {})
    }
  });

  // Extract evidence from similar jobs results
  const evidenceList = useMemo(() => {
    if (!similarJobs || similarJobs.length === 0) return [];
    
    const evidenceMap = new Map();
    
    similarJobs.forEach(job => {
      if (job.evidenceFiles) {
        job.evidenceFiles.forEach(evidence => {
          const key = evidence.id || evidence.fileName || evidence.name;
          if (evidenceMap.has(key)) {
            evidenceMap.get(key).jobs.push({
              jobId: job.jobId,
              provider: job.provider,
              date: job.date,
              casefileId: job.casefileId,
              similarityScore: job.similarityScore
            });
          } else {
            evidenceMap.set(key, {
              ...evidence,
              jobs: [{
                jobId: job.jobId,
                provider: job.provider,
                date: job.date,
                casefileId: job.casefileId,
                similarityScore: job.similarityScore
              }]
            });
          }
        });
      }
    });
    
    return Array.from(evidenceMap.values());
  }, [similarJobs]);

  // Handle evidence click - pass to parent to handle modal switching
  const handleEvidenceClick = (evidence) => {
    console.log('🎬 Requesting evidence modal from Similar Jobs', {
      evidenceId: evidence.id,
      evidenceName: evidence.fileName,
      context: 'from_similar_jobs'
    });
    
    // Let parent handle the modal switching (close results, open evidence)
    if (onOpenEvidence) {
      onOpenEvidence(evidence);
    }
  };

  // Loading state
  if (loading && isEmpty) {
    return (
      <div className={`similar-jobs-panel ${className}`}>
        <div className="panel-header">
          <h3>Similar Jobs</h3>
        </div>
        <div className="loading-state">
          <FaSync className="spinning" />
          <p>Finding similar jobs...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (isEmpty && !loading) {
    return (
      <div className={`similar-jobs-panel ${className}`}>
        <div className="panel-header">
          <h3>Similar Jobs</h3>
        </div>
        <div className="empty-state">
          <p>No similar jobs found for this analysis.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`similar-jobs-panel ${className}`}>
      {/* Header */}
      <div className="panel-header">
        <div className="header-left">
          <h3>Similar Jobs</h3>
          {hasResults && (
            <span className="results-count">
              {similarJobs.length} found
            </span>
          )}
        </div>
        <div className="header-right">
          {loading && <FaSync className="spinning" />}
          {refresh && (
            <button onClick={refresh} className="refresh-btn" title="Refresh">
              <FaSync />
            </button>
          )}
        </div>
      </div>

      {/* Results Tabs - Same structure as UnifiedResultsPanel */}
      <div className="results-tabs">
        <button
          className={`results-tab ${activeTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          Jobs
          <span className="results-tab-count">{similarJobs.length}</span>
        </button>
        <button
          className={`results-tab ${activeTab === 'evidence' ? 'active' : ''}`}
          onClick={() => setActiveTab('evidence')}
        >
          Evidence
          <span className="results-tab-count">{evidenceList.length}</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'jobs' && (
          <UnifiedSimilarResults
            results={similarJobs}
            loading={loading}
            error={error}
            viewMode="list"
            context="panel"
            onResultSelect={onJobSelect}
            onRefresh={refresh}
            title=""
            showHeader={false}
            showFilters={true}
            showStats={showStatistics}
            className="embedded-results"
            currentUser={currentUser}
          />
        )}

        {activeTab === 'evidence' && (
          <div className="evidence-tiles-container">
            {evidenceList.length === 0 ? (
              <div className="empty-state">
                <p>No evidence files found in similar jobs.</p>
              </div>
            ) : (
              evidenceList.map((evidence) => {
                const actionButtons = getEvidenceActions('public', {
                  onOpenResults: () => {
                    // Results button intentionally disabled in this context
                    // (already in results modal)
                  },
                  onOpenScan: () => handleEvidenceClick(evidence)
                });

                return (
                  <UniversalEvidenceWindow
                    key={evidence.id || evidence.fileName}
                    evidence={evidence}
                    mode="tile"
                    actionButtons={actionButtons}
                    onOpenModal={() => handleEvidenceClick(evidence)}
                    currentUser={null}
                    casefileName={`Similar Jobs (${evidence.jobs?.length || 0} matches)`}
                  />
                );
              })
            )}
          </div>
        )}
      </div>

    </div>
  );
};

export default SimilarJobsPanel;